import React, { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  gray100,
  gray90,
  primaryButtonBackgroundColor
} from '../../../constants/Colors';
import { headerHeight, spHeaderHeight } from '../../../constants/Location';
import { common } from '../../../modules/error';
import CustomLoading from '../../uiParts/CustomLoading';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import CustomTextfield from '../../uiParts/CustomTextfield';
import Header from '../../uiParts/Header';
import { userPasswordResetSchema } from '../../../validation/user/index';
import { UserPasswordResetType } from '../../../validation/user/userType';
import { response } from '../../../api/db';
import { sendMailForResetPassword } from '../../../api/db/user';
import CustomHead from '../CustomHead';
import { sentryLog } from '../../../config/sentry';

const styles = {
  button: {
    height: '44px',
    fontSize: '16px'
  },
  container: {
    '@media screen and (max-width:600px)': {
      height: `calc( 100vh - ${spHeaderHeight} - 24px)`,
      m: `${spHeaderHeight} auto 0`,
      pt: '24px',
      width: '90%'
    },
    height: `calc( 100vh - ${headerHeight} - 56px)`,
    m: `${headerHeight} auto 0`,
    pt: '56px',
    width: '560px'
  },
  text: {
    color: gray90,
    m: '16px 0 24px'
  },
  title: {
    '@media screen and (max-width:600px)': {
      fontSize: '24px'
    },
    color: gray100,
    fontSize: '32px',
    fontWeight: '500',
    mb: '24px'
  }
};

const AuthPasswordReset: React.FC = () => {
  const navigate = useNavigate();

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch
  } = useForm<UserPasswordResetType>({
    resolver: yupResolver(userPasswordResetSchema)
  });

  const buttonDisabledFlag = (): boolean => {
    return watch('email') === undefined || '' || Boolean(errors.email);
  };

  const [errorText, setErrorText] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      await sendMailForResetPassword(watch('email'));
      navigate('/auth/password_reset/sent', {
        state: { email: watch('email') }
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const loginErrorResponse = axiosError.response as response;
        setErrorText(loginErrorResponse.data.message);
        setOpen(true);
      } else {
        setErrorText(common);
        setOpen(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomHead title="パスワード忘れ | CMCOS[コムコス]" />
      <CustomLoading loading={loading} />
      <Header />
      <Typography component="div" sx={styles.container}>
        <Typography sx={styles.title}>パスワード再設定</Typography>
        <Typography sx={styles.text}>
          登録メールアドレスをご入力ください。
          <br />
          パスワード再設定用のURLをメールでお送りいたします。
        </Typography>

        <Controller
          render={({ field }) => (
            <CustomTextfield
              {...field}
              error={Boolean(errors.email)}
              formMb="40px"
              helperText={errors.email?.message}
              labelText="メールアドレス"
              requiredText="必須"
            />
          )}
          control={control}
          name="email"
        />

        <Button
          disabled={buttonDisabledFlag()}
          fullWidth
          onClick={() => {
            void (async () => {
              await handleSubmit(async () => await onSubmit())();
            })();
          }}
          sx={[primaryButtonBackgroundColor, styles.button]}
          variant="contained"
        >
          再設定用URLを送る
        </Button>
      </Typography>
      <CustomSnackbar
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
        open={open}
        text={errorText}
      />
    </>
  );
};

export default AuthPasswordReset;
